import axios, { AxiosError, AxiosResponse } from "axios";
import { getAuthUser, setAuthUser } from "src/services/Authentication/AuthenticationService";
import { AuthAPIService } from "src/api/Authentication/API_AuthService";
import store from '../store/index';
import { refreshTokens, lock } from "src/store/Authentication/authActions";
import { IApiResponse } from "../shared/models/IApiResponse";
import { alertFromAPIResponse } from "src/shared/services/AlertService";
import { getStoresXUser } from "src/services/Stores/FilteredStoreService";

const axiosApiInstance = axios.create();
axiosApiInstance.defaults.baseURL = process.env.REACT_APP_RETAIL_API_URL;

// Proactively refresh when a successful response arrives and the token has less than 2 minutes left.
// This covers active users whose calls succeed before the 401 window.
const PROACTIVE_THRESHOLD_MS = 2 * 60 * 1000;
let proactiveRefreshInProgress = false;

const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const axiosDefaultHeaders = (token: string) => {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
    'ngrok-skip-browser-warning': 'true'
  };
};

// Append Token
axiosApiInstance.interceptors.request.use(
  async config => {
    const token = getAuthUser().user?.jwtToken || "";
    config.headers = axiosDefaultHeaders(token);
    const storesXUser = getStoresXUser();
    if(storesXUser != null)
      config.headers['StoresXUser'] = storesXUser.length > 0 ?  storesXUser.map((x) => x.storeId).join(',') : '';

    return config;
  },
  error => {
    Promise.reject(error);
  }
);

axiosApiInstance.interceptors.response.use(
  (response): any => {
    const responseData = response.data as IApiResponse;
    const method = response.config?.method?.toLowerCase();

    if (method && method !== "get") {
      alertFromAPIResponse(responseData);
    }

    // If the token is within the proactive threshold, refresh in background so
    // active users are never interrupted by the lock screen.
    if (!proactiveRefreshInProgress) {
      const authUser = getAuthUser();
      const token = authUser.user?.jwtToken;
      const rt = authUser.user?.refreshToken;
      if (token && rt && authUser.logged) {
        const exp = decodeJwtExp(token);
        if (exp && exp - Date.now() < PROACTIVE_THRESHOLD_MS) {
          proactiveRefreshInProgress = true;
          AuthAPIService.refreshToken(rt)
            .then(auth => {
              const newState = { user: auth, logged: true, isLocked: false };
              setAuthUser(newState);
              store.dispatch(refreshTokens(auth));
            })
            .finally(() => { proactiveRefreshInProgress = false; });
        }
      }
    }

    return response;
  },
  async function (err) {
    const error = err as AxiosError;
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      const { data } = (error.response as AxiosResponse<IApiResponse>) || {};
      alertFromAPIResponse(data);
      return Promise.reject(error);
    }

    const authUser = getAuthUser();
    const refreshToken = authUser.user?.refreshToken || "";

    if (!refreshToken || !authUser.logged) {
      store.dispatch(lock());
      return Promise.reject(error);
    }

    if ((originalRequest as any)._retry) {
      store.dispatch(lock());
      return Promise.reject(error);
    }

    (originalRequest as any)._retry = true;

    return await AuthAPIService.refreshToken(refreshToken)
      .then(auth => {
        const newState = { user: auth, logged: true, isLocked: false };
        setAuthUser(newState);
        store.dispatch(refreshTokens(auth));
        axiosApiInstance.defaults.headers = axiosDefaultHeaders(auth.jwtToken);
        originalRequest.headers = axiosDefaultHeaders(auth.jwtToken);
        (originalRequest as any)._retry = false;

        return Promise.resolve(axiosApiInstance(originalRequest));
      })
      .catch(() => {
        store.dispatch(lock());
        return Promise.reject(error);
      });
  }
);

export default axiosApiInstance;
